import { ALL_TYPES, BODY_K } from './enemies';
import { BOSSES } from './bosses';
import { ROSTER_IDS } from './boss-roster';
import { bossFieldScale } from './boss-stats';
/* ============================================================
   圖鑑的清單來源（40 隻小怪 + BOSS）
   ============================================================

   拆成獨立一個檔的理由：`data/bosses.ts` 是**機械產生的**（不能手改），
   而「圖鑑要列哪些」是產品決定，兩者不該住在一起。

   ## Boss 在玩家模式與開發模式看到的不一樣（使用者 2026-08-03 拍板）

   原型有 23 隻，第一關只上四隻（`data/boss-roster.ts`）。
   23 隻全列進玩家模式的話，第一次打開圖鑑會看到 19 個永遠解不開的問號 ——
   那不是「遠期目標」，那是雜訊。所以：

     玩家模式   只列排進時刻表的 Boss（仍然走三段解鎖）
     開發模式   23 隻全部（圖鑑同時是驗收台，要看得到剛移植好的東西）

   之後新增 Boss 只要排進 `BOSS_ROSTER`，自動就會進玩家清單。

   ## `fam` 被改寫成 `'boss'`

   規格 §5.2「家族歸屬」那一段講得很清楚：**BOSS 不受家族約束是刻意的** ——
   家族是給「一次出現二十隻」的東西用的規則。有幾隻 Boss 的欄位表裡帶了
   `fam`（KATHEDRALE 是 wall、SICHEL 是 bar…），那是原型拿來排 UI 的，
   不代表牠們屬於那一族。圖鑑照規格把牠們全部收進獨立的 BOSS 分組。 */

/** 每一筆都帶**世界尺寸**：`bodyK`（倍率）、`worldH`/`worldW`（世界單位）。

    ⚠️ **這是圖鑑這一側的唯一事實來源，消費端不准自己乘 `BODY_K`。**
    造型表的 `h`/`w` 是「scale 1 時的尺寸」，要變成畫面上的大小得乘倍率 ——
    而倍率有兩種（小怪 `BODY_K`、大型 BOSS 走 `bossFieldScale` 壓縮）。
    2026-08-03 之前圖鑑的三個地方各自乘一次、而且乘的是寫死的 `BODY_K`，
    於是圖鑑的 KAISER 是 48 公尺、戰場上是 15 —— 同一隻東西兩個尺寸。
    在這裡算一次、其他地方只讀欄位，那種不一致就沒有發生的餘地。

    用 `Object.create` 包一層而不是直接寫進造型表：造型表是機械移植的，
    加欄位會讓移植腳本的 1:1 對照紅掉。 */
function withScale(T, bk) {
  T.bodyK = bk;
  T.worldH = T.h * bk;
  T.worldW = T.w * bk;
  return T;
}

/** 取這一筆的體型倍率。**圖鑑側只准走這裡** —— 有 fallback 是因為
    `barHTML()` 是 export 的，headless 測試可能餵造型表原件進來。 */
export function codexScale(T) { return (T && T.bodyK) || BODY_K; }

/** 小怪：一律 `BODY_K`（沒有一隻大到需要壓縮，最高的 HORCHER 是 4.84）。 */
var MOB_ENTRIES = (ALL_TYPES as any[]).map(function (D) {
  return withScale(Object.create(D), BODY_K);
});

/** Boss 在圖鑑裡的樣子：欄位表原封不動，只加清單用的標記與世界尺寸。 */
var BOSS_ENTRIES = BOSSES.map(function (B) {
  var T = Object.create(B);
  T.boss = 1;
  T.fam = 'boss';                 // 分組用；牠原本的 `fam` 仍然讀得到（原型用）
  T.protoFam = B.fam || '';
  return withScale(T, bossFieldScale(B.h));
});

/** 圖鑑清單。`dev` 為真時 Boss 全開，否則只有排進時刻表的那幾隻。 */
export function codexList(dev?) {
  var bosses = dev ? BOSS_ENTRIES
    : BOSS_ENTRIES.filter(function (T) { return ROSTER_IDS.indexOf(T.id) >= 0; });
  return MOB_ENTRIES.concat(bosses);
}

/** 開發模式的完整清單（`typeById` 用它 —— 切換開發模式時選中的那一隻
    不該憑空消失，只是列不列出來而已）。 */
export var CODEX_ALL = codexList(true);
